import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import Nav from './components/Nav';
import { Spinner } from './components/ui';
import { CurrencyProvider } from './lib/currency';
import { useSession } from './lib/session';
import Login from './routes/Login';
import Register from './routes/Register';
import ResetPassword from './routes/ResetPassword';

// Route-level code splitting. The login and register screens must not pay for
// Chart.js or the projection UI (§0: < 200KB gzipped to the browser).
const Summary = lazy(() => import('./routes/Summary'));
const Holdings = lazy(() => import('./routes/Holdings'));
const Goals = lazy(() => import('./routes/Goals'));
const Calculators = lazy(() => import('./routes/Calculators'));
const Insights = lazy(() => import('./routes/Insights'));
const Settings = lazy(() => import('./routes/Settings'));

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner label="Loading FIRE Tracker" />
    </div>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'anonymous') {
    // Remember where they were headed so sign-in can return them there.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const { status } = useSession();
  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'authenticated') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AppShell({ children }: { children: ReactNode }) {
  const { user } = useSession();
  return (
    <CurrencyProvider base={user?.baseCurrency ?? 'INR'}>
      <a href="#main" className="sr-only-focusable">
        Skip to main content
      </a>
      <Nav />
      <main id="main" className="mx-auto w-full max-w-6xl px-3 py-4 sm:px-4 sm:py-6">
        <Suspense fallback={<Spinner />}>{children}</Suspense>
      </main>
    </CurrencyProvider>
  );
}

export default function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RedirectIfSignedIn>
            <Login />
          </RedirectIfSignedIn>
        }
      />
      <Route
        path="/register"
        element={
          <RedirectIfSignedIn>
            <Register />
          </RedirectIfSignedIn>
        }
      />
      <Route path="/reset" element={<ResetPassword />} />

      <Route
        path="/*"
        element={
          <RequireAuth>
            <AppShell>
              <Routes>
                <Route path="/" element={<Summary />} />
                <Route path="/holdings" element={<Holdings />} />
                <Route path="/goals" element={<Goals />} />
                <Route path="/calculators" element={<Calculators />} />
                <Route path="/insights" element={<Insights />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AppShell>
          </RequireAuth>
        }
      />
    </Routes>
  );
}
